import {
  Autocomplete,
  InputLabel,
  TextField,
  Checkbox,
  Box,
  CircularProgress,
  styled,
  Popper,
  Tooltip
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import React, { useEffect, useRef } from 'react';
import { DropdownOptions, IFormController } from '../types';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@mui/icons-material/Info';
import Theme from '../../../Themes/main.theme';

const icon = <CheckBoxOutlineBlankIcon fontSize='small' />;
const checkedIcon = <CheckBoxIcon fontSize='small' />;

const StyledAutocomplete = styled(Autocomplete)`
    fieldset {
      border-radius: 24px;
    }
    .MuiChip-label {
      max-width: 300px;
    }
  `;

export interface MyMultiSelectOptions extends IFormController {
  customLabel?: boolean;
  multiple?: boolean;
  disableClearable?: boolean;
  freeSolo?: boolean;
  id?: string;
  groupByElement?: string;
  filtersDesign?: boolean;
  selectedItem?: any;
  required?: boolean;
  error?: any;
}

const MyMultiSelect = (props: MyMultiSelectOptions) => {
  const { label, customLabel, info, options, groupByElement = '', required, selectedItem,
    filtersDesign, isLoading, ...rest } = props;
    
  const [value, setValue] = React.useState(
    props.formik?.initialValues?.[props.name]
      ? props.formik?.initialValues?.[props.name]
      : []
  );
  const { t } = useTranslation();
  const reference = useRef<HTMLDivElement>(null);
  const PopperMy = React.useCallback((props: any) => {
    return <Popper {...props}
      style={{ width: reference?.current?.clientWidth, position: 'absolute', backgroundColor: 'inherit' }}
      placement='bottom-start'
    />;
  }, []);

  const emptyMsg = isLoading ? 
    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
      <CircularProgress size={20} />
    </Box>
    : t('Μη διαθέσιμες επιλογές');

  const initialValueInOptions = (options: DropdownOptions[] | any, field: DropdownOptions) =>
    options?.some((x: any) => x.value == field.value);

  const handleChange = (values: any) => {
    if (values.length > 0) {
      props?.formik?.setFieldValue(props.name, values);
      setValue(values);
      selectedItem && selectedItem(values);
    } else {
      props?.formik?.setFieldValue(props.name, values);
      selectedItem && selectedItem(values);
      setValue([]);
    }
    props?.formik?.setTouched({ [props.name]: true });
  };

  useEffect(() => {
    if (props?.formik?.initialValues[props.name]
      && props?.formik?.initialValues[props.name].length > 0) {
      const values: DropdownOptions[] =
        props.formik?.initialValues?.[props.name].map((field: DropdownOptions) => {
          if (initialValueInOptions(options, field)) {
            return options?.find((x: any) => x.value == field.value);
          }
        }).filter((x: any) => x);
        
      // const ids = values.map((value) => value.value);
      // props.formik.setFieldValue(props.name, ids);
      // setValue(values);

      handleChange(values);
    }
  }, [props?.formik?.initialValues[props.name]]);

  useEffect(() => {
    if (props?.formik?.values[props.name]
      && props?.formik?.values[props.name].length > 0) {
      const values: DropdownOptions[] =
        props.formik?.values?.[props.name].map((field: DropdownOptions) => {
          if (initialValueInOptions(options, field)) {
            return options?.find((x: any) => x.value == field.value);
          }
        }).filter((x: any) => x);
        
      // const ids = values.map((value) => value.value);
      // props.formik.setFieldValue(props.name, ids);
      // setValue(values);

      handleChange(values);
    } else if (props?.formik?.initialValues[props.name]
      && props?.formik?.initialValues[props.name].length > 0) {
      const values: DropdownOptions[] =
        props.formik?.initialValues?.[props.name].map((field: DropdownOptions) => {
          if (initialValueInOptions(options, field)) {
            return options?.find((x: any) => x.value == field.value);
          }
        }).filter((x: any) => x);
        
      // const ids = values.map((value) => value.value);
      // props.formik.setFieldValue(props.name, ids);
      // setValue(values);

      handleChange(values);
    }
  }, [options]);


  return (
    <Box>
      {
        customLabel
          ? <Box display='flex' alignItems='center'> 
            <InputLabel
              htmlFor={props.name}
              disabled={props.disabled}
              required={required}
              sx={{
                my: customLabel
                  ? 2
                  : 0,
                textAlign: 'left',
                textStyle: 'none',
              }}
            >
              {label}
            </InputLabel>
            {info ? 
              <Tooltip
                PopperProps={{
                  disablePortal: true
                }} title={props?.tooltip}
                placement='right-start'>
                <InfoIcon sx={{color: Theme?.iconColor?.main, ml: 1, fontSize: '17px'}}/>
              </Tooltip>
              : null
            }
          </Box> 
          : null
      }
      {filtersDesign
        ? <StyledAutocomplete
          fullWidth
          groupBy={(option: any) => option[groupByElement]}
          limitTags={2}
          {...rest}
          noOptionsText={emptyMsg}
          isOptionEqualToValue={(option: any, value: any) => option.value == value.value}
          value={props?.formik?.values[props.name]
            ? value
            : []}
          disablePortal
          multiple
          options={options?.map(x=> {
            return {
              ...x,
              key: t(x.key)
            };
          }) || []}
          disableCloseOnSelect
          onChange={(_, values) => handleChange(values)}
          getOptionLabel={(option: any) => option.key}
          renderOption={(props, option: any, { selected }) => (
            <li {...props} style={{ backgroundColor: 'transparent'}}>
              <Checkbox
                icon={icon}
                checkedIcon={checkedIcon}
                style={{ marginRight: 8 }}
                checked={selected}
              />
              {option.key}
            </li>
          )}
          style={{ minWidth: 200 }}
          renderInput={(params: any) => (
            <TextField
              {...params}
              label={customLabel
                ? null
                : label}
            />
          )}
        />
        :
        <div ref={reference}
          style={{ position: 'relative' }} >
          <Autocomplete
            fullWidth
            groupBy={(option: any) => option[groupByElement]}
            limitTags={2}
            PopperComponent={PopperMy}
            {...rest}
            noOptionsText={emptyMsg}
            isOptionEqualToValue={(option, value) => option.value == value.value}
            value={value}
            disablePortal
            multiple
            options={options || []}
            disableCloseOnSelect
            onChange={(_, values) => handleChange(values)}
            getOptionLabel={(option: any) => option.key}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8 }}
                  checked={selected}
                />
                {option.key}
              </li>
            )}
            renderInput={(params) => (
              <TextField 
                {...params}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <>
                    {props.startAdornment}
                    {params.InputProps.startAdornment}
                  </>
                  ,
                }}
                label={customLabel
                  ? null
                  : label}
              />
            )}
          />
        </div>

      }
    </Box>
  );
};

export default MyMultiSelect;